(() => {
  const root = document.querySelector('[data-aonsk-donation]');
  if (!root) return;

  const form = root.querySelector('.aonsk-donation__form');
  if (!form) return;
  document.documentElement.classList.add('aonsk-donation-js');

  const steps = Array.from(form.querySelectorAll('[data-donation-step]'));
  const maxStep = steps.length;
  const nextButton = form.querySelector('[data-donation-next]');
  const backButton = form.querySelector('[data-donation-back]');
  const submitButton = form.querySelector('[data-donation-submit]');
  const progressLabel = form.querySelector('[data-donation-progress-label]');
  const progressTitle = form.querySelector('[data-donation-progress-title]');
  const progressBar = form.querySelector('[data-donation-progress-bar]');
  const progressSteps = Array.from(form.querySelectorAll('[data-donation-progress-step]'));
  const review = form.querySelector('[data-donation-review]');
  const saveToggle = form.querySelector('[data-donation-save-progress]');
  const draftBanner = form.querySelector('[data-donation-draft-banner]');
  const restoreButton = form.querySelector('[data-donation-draft-restore]');
  const discardButton = form.querySelector('[data-donation-draft-discard]');
  const successMessage = form.querySelector('[data-donation-success]');
  const draftKey = 'aonskSeedDonationDraftV1042';
  let currentStep = 1;
  let saveTimer = null;

  const safelyParse = (value) => {
    try { return JSON.parse(value); } catch (_error) { return null; }
  };

  const getStoredDraft = () => {
    try { return safelyParse(window.localStorage.getItem(draftKey)); } catch (_error) { return null; }
  };

  const removeStoredDraft = () => {
    try { window.localStorage.removeItem(draftKey); } catch (_error) { /* Storage can be unavailable. */ }
  };

  if (successMessage) removeStoredDraft();

  const conditionControllers = Array.from(form.querySelectorAll('[data-condition-controller]'));
  const conditionalGroups = Array.from(form.querySelectorAll('[data-condition-field]'));

  const selectedConditionValues = (key) => conditionControllers
    .filter((control) => control.dataset.conditionController === key && control.checked)
    .map((control) => control.value);

  const updateConditionals = () => {
    conditionalGroups.forEach((group) => {
      const key = group.dataset.conditionField || '';
      const accepted = (group.dataset.conditionValues || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      const currentValues = selectedConditionValues(key);
      const active = currentValues.some((value) => accepted.includes(value));
      group.classList.toggle('is-active', active);
      group.setAttribute('aria-hidden', active ? 'false' : 'true');
      group.querySelectorAll('input, select, textarea').forEach((field) => {
        field.disabled = !active;
        if (!active && field.matches('[required]')) field.dataset.wasRequired = 'true';
        if (!active) field.required = false;
        if (active && field.dataset.wasRequired === 'true') field.required = true;
      });
    });
  };

  conditionControllers.forEach((control) => control.addEventListener('change', updateConditionals));

  const firstFocusableInvalid = (scope) => scope.querySelector('input:invalid:not([disabled]), select:invalid:not([disabled]), textarea:invalid:not([disabled])');

  const validateRequiredCheckboxGroups = (step, shouldFocus = true) => {
    let valid = true;
    step.querySelectorAll('[data-required-checkbox-group]').forEach((group) => {
      const name = group.dataset.requiredCheckboxGroup;
      const anyChecked = Array.from(group.querySelectorAll('input[type="checkbox"]:not([disabled])')).some((field) => field.checked);
      const error = group.querySelector(`[data-group-error="${name}"]`);
      if (error) error.hidden = anyChecked;
      group.classList.toggle('has-error', !anyChecked);
      if (!anyChecked && valid) {
        valid = false;
        if (shouldFocus) {
          group.querySelector('input[type="checkbox"]')?.focus({ preventScroll: true });
          group.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    });
    return valid;
  };

  form.querySelectorAll('[data-required-checkbox-group] input[type="checkbox"]').forEach((field) => {
    field.addEventListener('change', () => {
      const group = field.closest('[data-required-checkbox-group]');
      if (!group) return;
      const anyChecked = Array.from(group.querySelectorAll('input[type="checkbox"]')).some((item) => item.checked);
      const error = group.querySelector('[data-group-error]');
      if (error) error.hidden = anyChecked;
      group.classList.toggle('has-error', !anyChecked);
    });
  });

  const validateStep = (stepNumber, shouldFocus = true) => {
    const step = steps[stepNumber - 1];
    if (!step) return true;
    updateConditionals();

    const checkboxGroupsValid = validateRequiredCheckboxGroups(step, shouldFocus);
    const invalid = firstFocusableInvalid(step);
    if (invalid) {
      if (shouldFocus) {
        invalid.reportValidity();
        invalid.focus({ preventScroll: true });
        invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return false;
    }
    return checkboxGroupsValid;
  };

  const labelFromName = (name) => name
    .replace(/^contact\[/, '')
    .replace(/\]$/, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

  const valueFromControl = (control) => {
    if (control.type === 'checkbox' || control.type === 'radio') return control.checked ? control.value : '';
    if (control.tagName === 'SELECT') return control.options[control.selectedIndex]?.text || '';
    return String(control.value || '').trim();
  };

  const buildReview = () => {
    if (!review) return;
    review.textContent = '';
    const headings = ['Contact and project', 'Community and purpose', 'Growing readiness', 'Seed request', 'Impact and relationship'];

    steps.slice(0, 5).forEach((step, index) => {
      const card = document.createElement('section');
      card.className = 'aonsk-donation__review-card';
      const heading = document.createElement('h3');
      heading.textContent = headings[index];
      card.appendChild(heading);
      const list = document.createElement('dl');
      const seen = new Set();

      const addEntry = (label, value) => {
        const cleanValue = String(value || '').trim();
        const cleanLabel = String(label || '').trim();
        if (!cleanLabel || !cleanValue || seen.has(`${cleanLabel}|${cleanValue}`)) return;
        seen.add(`${cleanLabel}|${cleanValue}`);
        const wrapper = document.createElement('div');
        const term = document.createElement('dt');
        const detail = document.createElement('dd');
        term.textContent = cleanLabel;
        detail.textContent = cleanValue;
        wrapper.append(term, detail);
        list.appendChild(wrapper);
      };

      step.querySelectorAll('[data-summary-label]:not([disabled])').forEach((control) => {
        addEntry(control.dataset.summaryLabel, valueFromControl(control));
      });

      const radioNames = new Set(Array.from(step.querySelectorAll('input[type="radio"]:checked:not([disabled])')).map((control) => control.name));
      radioNames.forEach((name) => {
        const control = step.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked:not([disabled])`);
        if (control) addEntry(labelFromName(name), control.value);
      });

      const checkedCategories = Array.from(step.querySelectorAll('.aonsk-donation__checkbox-grid input[type="checkbox"]:checked:not([disabled])'))
        .map((control) => control.closest('label')?.textContent?.trim())
        .filter(Boolean);
      if (checkedCategories.length) addEntry('Seed categories', checkedCategories.join(', '));

      if (!list.children.length) addEntry('Status', 'No additional information entered.');
      card.appendChild(list);
      review.appendChild(card);
    });
  };

  const serializeDraft = () => {
    const values = {};
    const controls = Array.from(form.elements).filter((control) => {
      if (!control.name || !control.name.startsWith('contact[')) return false;
      if (['contact[body]', 'contact[Application source]', 'contact[Application submitted]', 'contact[Application type]'].includes(control.name)) return false;
      return true;
    });

    controls.forEach((control) => {
      if (control.type === 'radio' || control.type === 'checkbox') {
        if (!control.checked) return;
        if (!Array.isArray(values[control.name])) values[control.name] = [];
        values[control.name].push(control.value);
      } else {
        values[control.name] = control.value;
      }
    });

    return { version: 1042, savedAt: Date.now(), currentStep, values };
  };

  const saveDraft = () => {
    if (!saveToggle?.checked) return;
    try { window.localStorage.setItem(draftKey, JSON.stringify(serializeDraft())); } catch (_error) { /* Storage can be unavailable. */ }
  };

  const scheduleSave = () => {
    if (!saveToggle?.checked) return;
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(saveDraft, 250);
  };

  const restoreDraft = (draft) => {
    if (!draft?.values) return;
    Object.entries(draft.values).forEach(([name, value]) => {
      const controls = Array.from(form.querySelectorAll(`[name="${CSS.escape(name)}"]`));
      if (!controls.length) return;
      if (Array.isArray(value)) {
        controls.forEach((control) => { control.checked = value.includes(control.value); });
      } else {
        controls.forEach((control) => { control.value = value; });
      }
    });
    if (saveToggle) saveToggle.checked = true;
    updateConditionals();
    setStep(Math.min(Math.max(Number(draft.currentStep) || 1, 1), maxStep), false);
    saveDraft();
  };

  const storedDraft = getStoredDraft();
  if (storedDraft && draftBanner && !successMessage) draftBanner.hidden = false;

  restoreButton?.addEventListener('click', () => {
    restoreDraft(getStoredDraft());
    if (draftBanner) draftBanner.hidden = true;
  });

  discardButton?.addEventListener('click', () => {
    removeStoredDraft();
    if (draftBanner) draftBanner.hidden = true;
    if (saveToggle) saveToggle.checked = false;
  });

  saveToggle?.addEventListener('change', () => {
    if (saveToggle.checked) saveDraft();
    else removeStoredDraft();
  });

  form.addEventListener('input', scheduleSave);
  form.addEventListener('change', scheduleSave);

  const setStep = (stepNumber, shouldScroll = true) => {
    currentStep = Math.min(Math.max(stepNumber, 1), maxStep);
    steps.forEach((step, index) => step.classList.toggle('is-active', index + 1 === currentStep));
    progressSteps.forEach((item, index) => {
      item.classList.toggle('is-current', index + 1 === currentStep);
      item.classList.toggle('is-complete', index + 1 < currentStep);
    });
    const active = steps[currentStep - 1];
    if (progressLabel) progressLabel.textContent = `Step ${currentStep} of ${maxStep}`;
    if (progressTitle) progressTitle.textContent = active?.dataset.stepTitle || '';
    if (progressBar) progressBar.style.width = `${(currentStep / maxStep) * 100}%`;
    if (backButton) backButton.hidden = currentStep === 1;
    if (nextButton) nextButton.hidden = currentStep === maxStep;
    if (submitButton) submitButton.hidden = currentStep !== maxStep;
    if (currentStep === maxStep) buildReview();
    updateConditionals();
    saveDraft();
    if (shouldScroll) {
      form.querySelector('[data-donation-progress]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.setTimeout(() => active?.querySelector('input:not([type="hidden"]), select, textarea')?.focus({ preventScroll: true }), 350);
    }
  };

  nextButton?.addEventListener('click', () => {
    if (!validateStep(currentStep)) return;
    setStep(currentStep + 1);
  });

  backButton?.addEventListener('click', () => setStep(currentStep - 1));

  form.addEventListener('submit', (event) => {
    updateConditionals();
    for (let index = 1; index <= maxStep; index += 1) {
      if (!validateStep(index, false)) {
        event.preventDefault();
        setStep(index, false);
        window.requestAnimationFrame(() => {
          const step = steps[index - 1];
          const invalid = firstFocusableInvalid(step);
          const invalidGroup = step?.querySelector('.has-error');
          if (invalid) {
            invalid.reportValidity();
            invalid.focus({ preventScroll: true });
            invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
          } else if (invalidGroup) {
            invalidGroup.querySelector('input[type="checkbox"]')?.focus({ preventScroll: true });
            invalidGroup.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        });
        return;
      }
    }
    if (!form.checkValidity()) return;
    if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = 'Submitting application…';
      window.setTimeout(() => {
        submitButton.disabled = false;
        submitButton.textContent = 'Submit seed donation request';
      }, 14000);
    }
  });

  updateConditionals();
  setStep(1, false);
})();
